import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi, twoFaApi } from '../services/api'
import toast from 'react-hot-toast'

export default function ProfiloSicurezza() {
  const qc = useQueryClient()
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => authApi.me() })
  const user = me?.data
  const totpEnabled: boolean = user?.totp_enabled ?? false

  const [qrData, setQrData] = useState<string | null>(null)
  const [secret, setSecret] = useState('')
  const [setupCode, setSetupCode] = useState('')
  const [disableCode, setDisableCode] = useState('')
  const [showDisable, setShowDisable] = useState(false)

  const setupMut = useMutation({
    mutationFn: () => twoFaApi.setup(),
    onSuccess: (res) => { setQrData(res.data.qr); setSecret(res.data.secret) },
    onError: () => toast.error('Errore avvio setup 2FA'),
  })

  const verifySetupMut = useMutation({
    mutationFn: () => twoFaApi.verifySetup(setupCode),
    onSuccess: () => {
      toast.success('2FA abilitata!')
      setQrData(null); setSetupCode(''); setSecret('')
      qc.invalidateQueries({ queryKey: ['me'] })
    },
    onError: () => toast.error('Codice non valido'),
  })

  const disableMut = useMutation({
    mutationFn: () => twoFaApi.disable(disableCode),
    onSuccess: () => {
      toast.success('2FA disabilitata')
      setDisableCode(''); setShowDisable(false)
      qc.invalidateQueries({ queryKey: ['me'] })
    },
    onError: () => toast.error('Codice non valido'),
  })

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '32px 20px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Sicurezza account</h1>
      <p style={{ color: '#64748b', marginBottom: 32 }}>{user?.email}</p>

      <div className="card" style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Autenticazione a due fattori (2FA)</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              Proteggi l'account con un codice TOTP (Google Authenticator, Aegis, ecc.)
            </div>
          </div>
          <span className={`badge ${totpEnabled ? 'badge-success' : ''}`}
            style={{ background: totpEnabled ? '#dcfce7' : '#f1f5f9', color: totpEnabled ? '#15803d' : '#64748b', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
            {totpEnabled ? 'Attiva' : 'Disattiva'}
          </span>
        </div>

        {!totpEnabled && !qrData && (
          <button className="btn-primary" onClick={() => setupMut.mutate()} disabled={setupMut.isPending}>
            {setupMut.isPending ? 'Caricamento...' : 'Abilita 2FA'}
          </button>
        )}

        {qrData && (
          <div>
            <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>
              Scansiona il QR code con la tua app autenticatore, poi inserisci il codice a 6 cifre per confermare.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <img src={qrData} alt="QR 2FA" style={{ width: 200, height: 200, border: '1px solid #e2e8f0', borderRadius: 8 }} />
            </div>
            <details style={{ marginBottom: 16 }}>
              <summary style={{ fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>Codice manuale</summary>
              <code style={{ fontSize: 13, letterSpacing: '0.15em', background: '#f8fafc', padding: '4px 8px', borderRadius: 4, display: 'block', marginTop: 6 }}>{secret}</code>
            </details>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={setupCode}
                onChange={e => setSetupCode(e.target.value.replace(/\D/g, ''))}
                style={{ flex: 1, letterSpacing: '0.3em', textAlign: 'center', fontSize: 18 }}
              />
              <button className="btn-primary" onClick={() => verifySetupMut.mutate()}
                disabled={setupCode.length !== 6 || verifySetupMut.isPending}>
                {verifySetupMut.isPending ? '...' : 'Conferma'}
              </button>
            </div>
            <button className="btn-ghost" style={{ marginTop: 8, fontSize: 13 }}
              onClick={() => { setQrData(null); setSetupCode(''); setSecret('') }}>
              Annulla
            </button>
          </div>
        )}

        {totpEnabled && !showDisable && (
          <button className="btn-secondary" onClick={() => setShowDisable(true)}>
            Disabilita 2FA
          </button>
        )}

        {totpEnabled && showDisable && (
          <div>
            <p style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>
              Inserisci il codice dall'app per disabilitare la 2FA:
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={disableCode}
                onChange={e => setDisableCode(e.target.value.replace(/\D/g, ''))}
                style={{ flex: 1, letterSpacing: '0.3em', textAlign: 'center', fontSize: 18 }}
              />
              <button className="btn-primary"
                style={{ background: '#dc2626' }}
                onClick={() => disableMut.mutate()}
                disabled={disableCode.length !== 6 || disableMut.isPending}>
                {disableMut.isPending ? '...' : 'Disabilita'}
              </button>
            </div>
            <button className="btn-ghost" style={{ marginTop: 8, fontSize: 13 }}
              onClick={() => { setShowDisable(false); setDisableCode('') }}>
              Annulla
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
